// src/pages/league-dashboard/MinorLeagueDraft.js - Minor League Draft Interface
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Users, Trophy, RefreshCw, CheckCircle, Clock,
  Play, Check, Search, Shield, ChevronUp, ChevronDown,
  Gavel, Target, Shuffle, List, BarChart3, Dices, Settings,
  AlertTriangle, Undo2, X, Calendar
} from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import TeamPageHeader from '../../components/common/TeamPageHeader';
import PlayerSearchWithManual from '../../components/draft/PlayerSearchWithManual';
import DraftBoard from '../../components/draft/DraftBoard';

// ========================================
// CONFIRM MODAL (replaces window.confirm)
// ========================================
const ConfirmModal = ({ isOpen, title, message, confirmLabel, confirmVariant = 'primary', onConfirm, onCancel }) => {
  if (!isOpen) return null;

  const confirmClass = confirmVariant === 'warning'
    ? `${dynastyTheme.utils.getComponent('button', 'ghost', 'md')} ${dynastyTheme.classes.text.error} hover:bg-red-500/20`
    : dynastyTheme.utils.getComponent('button', confirmVariant || 'primary', 'md');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div
        className={`${dynastyTheme.components.card.base} border ${dynastyTheme.classes.border.neutral} max-w-md w-full`}
        style={{
          borderRadius: dynastyTheme.tokens.radius.lg,
          padding: dynastyTheme.tokens.spacing.xl,
        }}
      >
        <div className="text-center" style={{ marginBottom: dynastyTheme.tokens.spacing.lg }}>
          <div
            className={`flex items-center justify-center mx-auto ${dynastyTheme.classes.bg.warning} ${dynastyTheme.classes.shadow.base}`}
            style={{
              width: '3rem',
              height: '3rem',
              borderRadius: dynastyTheme.tokens.radius.lg,
              marginBottom: dynastyTheme.tokens.spacing.md,
            }}
          >
            <AlertTriangle className={dynastyTheme.classes.text.black} style={{ width: '1.5rem', height: '1.5rem' }} />
          </div>
          <h3 className={`${dynastyTheme.components.heading.h2} ${dynastyTheme.classes.text.white} mb-2`}>
            {title}
          </h3>
        </div>

        <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-6 text-center`}>
          {message}
        </p>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className={`flex-1 ${dynastyTheme.utils.getComponent('button', 'secondary', 'md')} justify-center`}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 ${confirmClass} justify-center`}
          >
            {confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ========================================
// CONSTANTS
// ========================================
const ORDER_METHODS = [
  { value: 'linear', label: 'Linear', icon: List, description: 'Same order every round' },
  { value: 'snake', label: 'Snake', icon: Shuffle, description: 'Reverses each round' },
  { value: 'random', label: 'Random', icon: Dices, description: 'Randomized order' },
  { value: 'standings', label: 'Standings', icon: BarChart3, description: 'Worst team picks first (snake)' },
  { value: 'lottery', label: 'Lottery', icon: Target, description: 'Weighted lottery (snake)' },
  { value: 'manual', label: 'Manual', icon: Settings, description: 'Commissioner sets order' },
];

const CLOCK_OPTIONS = [
  { value: null, label: 'Off' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
  { value: 480, label: '8 hours' },
  { value: 720, label: '12 hours' },
  { value: 1440, label: '1 day' },
  { value: 2880, label: '2 days' },
  { value: 4320, label: '3 days' },
];

const STEPS = [
  { key: 'setup', label: 'Setup' },
  { key: 'order', label: 'Draft Order' },
  { key: 'drafting', label: 'Drafting' },
  { key: 'complete', label: 'Complete' },
];

// ========================================
// UPCOMING PICKS HELPER (snake-aware)
// ========================================
// Exported as a named export per Bible §14 frontend testing convention
// so unit tests can target the snake-reversal logic directly without
// exercising the full draft-state polling flow. Pure function — no
// side effects, no closures.
export const computeUpcomingPicks = (draftOrder, currentRound, currentPickInRound, totalRounds, method, count = 5) => {
  if (!draftOrder || draftOrder.length === 0) return [];
  const totalTeams = draftOrder.length;
  const isSnake = ['snake', 'standings', 'lottery'].includes(method);
  const sorted = [...draftOrder].sort((a, b) => a.pick_order - b.pick_order);

  const picks = [];
  let round = currentRound;
  let pickInRound = currentPickInRound;

  while (picks.length < count && round <= totalRounds) {
    const reversed = isSnake && round % 2 === 0;
    const teamIdx = reversed ? (totalTeams - pickInRound) : (pickInRound - 1);
    const team = sorted[teamIdx];

    if (team) {
      picks.push({
        round,
        pickInRound,
        teamId: team.team_id,
        teamName: team.team_name,
      });
    }

    pickInRound++;
    if (pickInRound > totalTeams) {
      pickInRound = 1;
      round++;
    }
  }

  return picks;
};

const MinorLeagueDraft = ({ leagueId, league, user, userTeam }) => {
  // ========================================
  // STATE MANAGEMENT
  // ========================================
  const [draftState, setDraftState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isInitializing, setIsInitializing] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isMyTurn, setIsMyTurn] = useState(false);
  const [currentTurnInfo, setCurrentTurnInfo] = useState(null);

  // Player selection state
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isPickingPlayer, setIsPickingPlayer] = useState(false);

  // Draft order configuration
  const [selectedMethod, setSelectedMethod] = useState('linear');
  const [isSettingOrder, setIsSettingOrder] = useState(false);
  const [manualOrder, setManualOrder] = useState(null);

  // Clock configuration (minutes)
  const [clockMinutes, setClockMinutes] = useState(null);
  const [isSettingClock, setIsSettingClock] = useState(false);

  // Commissioner override state
  const [commissionerTeamId, setCommissionerTeamId] = useState('');
  const [commissionerSelectedPlayer, setCommissionerSelectedPlayer] = useState(null);
  const [isCommissionerPicking, setIsCommissionerPicking] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);

  const [selectedYear, setSelectedYear] = useState(league?.current_season);

  // Sync if league loads after mount
  useEffect(() => {
    if (league?.current_season && selectedYear !== league.current_season) {
      setSelectedYear(league.current_season);
    }
  }, [league?.current_season]); // eslint-disable-line react-hooks/exhaustive-deps

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false, title: '', message: '', confirmLabel: '', confirmVariant: 'primary', onConfirm: null,
  });

  // Notification preferences
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [notifPrefsLoading, setNotifPrefsLoading] = useState(false);

  // Auto-refresh
  const pollIntervalRef = useRef(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const isCommissioner = league?.role === 'commissioner';
  const myTeamId = league?.team_id || null;

  // ========================================
  // YEAR OPTIONS
  // ========================================
  const yearOptions = useMemo(() => {
    const currentYear = league?.current_season;
    const years = [];
    for (let y = currentYear; y >= currentYear - 4; y--) {
      years.push(y);
    }
    return years;
  }, []);

  // ========================================
  // DRAFTED PLAYER IDS (for search indicator)
  // ========================================
  const draftedPlayerIds = useMemo(() => {
    if (!draftState?.picks || !Array.isArray(draftState.picks)) return new Set();
    return new Set(draftState.picks.map(p => p.player_id).filter(Boolean));
  }, [draftState?.picks]);

  // ========================================
  // UPCOMING PICKS + "PICKS AWAY" COMPUTATION
  // ========================================
  const upcomingPicks = useMemo(() => {
    if (!draftState || draftState.draft_status !== 'in_progress') return [];
    const grid = draftState.resolved_pick_grid ?? [];
    const totalTeams = draftState.draft_order?.length ?? 0;
    if (grid.length > 0 && totalTeams > 0) {
      const currentOverall = (draftState.current_round - 1) * totalTeams + draftState.current_pick_in_round;
      return grid
        .filter(e => e.overall_pick >= currentOverall && !e.is_picked)
        .sort((a, b) => a.overall_pick - b.overall_pick)
        .slice(0, 6)
        .map(e => ({ round: e.round_number, pickInRound: e.pick_in_round, teamId: e.team_id, teamName: e.team_name }));
    }
    return computeUpcomingPicks(
      draftState.draft_order,
      draftState.current_round,
      draftState.current_pick_in_round,
      draftState.total_rounds,
      draftState.draft_order_method || 'linear',
      6
    );
  }, [draftState]);

  const picksAwayFromMe = useMemo(() => {
    if (!myTeamId || upcomingPicks.length === 0) return null;
    const idx = upcomingPicks.findIndex(p => p.teamId === myTeamId);
    if (idx === -1) return null;
    return idx; // 0 = it's my turn, 1 = next, etc.
  }, [myTeamId, upcomingPicks]);

  // ========================================
  // PICK DEADLINE (server-computed)
  // ========================================
  const pickDeadline = useMemo(() => {
    if (!draftState || draftState.draft_status !== 'in_progress') return null;
    if (!draftState.current_pick_deadline) return null;
    return new Date(draftState.current_pick_deadline);
  }, [draftState]);

  // ========================================
  // CONFIRM MODAL HELPERS
  // ========================================
  const showConfirm = (title, message, confirmLabel, onConfirm, confirmVariant = 'primary') => {
    setConfirmModal({ isOpen: true, title, message, confirmLabel, confirmVariant, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmModal({ isOpen: false, title: '', message: '', confirmLabel: '', confirmVariant: 'primary', onConfirm: null });
  };

  // ========================================
  // STEPPER LOGIC
  // ========================================
  const getCurrentStep = () => {
    if (!draftState) return 0;
    if (draftState.draft_status === 'not_started') return 1;
    if (draftState.draft_status === 'in_progress') return 2;
    if (draftState.draft_status === 'completed') return 3;
    return 0;
  };

  // ========================================
  // DATA LOADING
  // ========================================
  const loadDraftState = useCallback(async (showLoadingSpinner = true) => {
    try {
      if (showLoadingSpinner) setLoading(true);
      setError('');

      const response = await leaguesAPI.getMinorLeagueDraft(leagueId, selectedYear);

      if (response && response.draft_id) {
        setDraftState(response);

        // Sync clock UI with server value
        if (response.draft_status === 'not_started') {
          setClockMinutes(response.pick_time_limit_minutes ?? null);
        }

        if (response.draft_status === 'in_progress') {
          try {
            const turnInfo = await leaguesAPI.checkMyMinorLeagueDraftTurn(leagueId, selectedYear);
            setIsMyTurn(turnInfo.is_your_turn || false);
            setCurrentTurnInfo(turnInfo);
          } catch (turnError) {
            console.error('Error checking turn:', turnError);
            setIsMyTurn(false);
          }
        } else {
          setIsMyTurn(false);
          setCurrentTurnInfo(null);
        }
      }
    } catch (err) {
      console.error('Error loading draft:', err);
      if (err.response?.status === 404) {
        setDraftState(null);
      } else {
        setError(err.response?.data?.detail || 'Failed to load draft');
      }
    } finally {
      if (showLoadingSpinner) setLoading(false);
    }
  }, [leagueId, selectedYear]);

  // ========================================
  // AUTO-REFRESH POLLING
  // ========================================
  useEffect(() => {
    loadDraftState();

    if (autoRefresh && draftState?.draft_status === 'in_progress') {
      pollIntervalRef.current = setInterval(() => {
        loadDraftState(false);
      }, 5000);
    }

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [leagueId, autoRefresh, draftState?.draft_status, loadDraftState]);

  // ========================================
  // MARK NOTIFICATIONS READ + LOAD PREFERENCES ON MOUNT
  // ========================================
  useEffect(() => {
    if (!leagueId) return;

    // Mark all draft notifications as read (clears badge)
    leaguesAPI.markDraftNotificationsRead(leagueId).catch(() => {});

    // Load notification preferences
    leaguesAPI.getDraftNotificationPreferences(leagueId)
      .then(prefs => setNotifPrefs(prefs))
      .catch(() => {});
  }, [leagueId]);

  const handleNotifPrefToggle = async (key) => {
    const updated = { ...notifPrefs, [key]: !notifPrefs[key] };
    setNotifPrefs(updated);
    try {
      await leaguesAPI.updateDraftNotificationPreferences(leagueId, updated);
    } catch {
      // Revert on failure
      setNotifPrefs(notifPrefs);
    }
  };

  // ========================================
  // DRAFT ACTIONS
  // ========================================
  const handleInitializeDraft = () => {
    showConfirm(
      'Initialize Draft',
      `Initialize Minor League Draft for ${selectedYear}? This will create a 2-round draft.`,
      'Initialize',
      async () => {
        closeConfirm();
        setIsInitializing(true);
        setError('');

        try {
          const response = await leaguesAPI.initializeMinorLeagueDraft(leagueId, selectedYear, 2);

          if (response.success) {
            setSuccessMessage('Draft initialized! Configure draft order, then start the draft.');
            setTimeout(() => setSuccessMessage(''), 4000);
            loadDraftState();
          } else {
            setError(response.message || 'Failed to initialize draft');
          }
        } catch (err) {
          console.error('Error initializing draft:', err);
          setError(err.response?.data?.detail || 'Failed to initialize draft');
        } finally {
          setIsInitializing(false);
        }
      }
    );
  };

  const handleSetDraftOrder = async (method) => {
    setIsSettingOrder(true);
    setError('');

    try {
      const order = method === 'manual' ? manualOrder : null;
      const response = await leaguesAPI.setMinorLeagueDraftOrder(leagueId, method, order);

      if (response.success) {
        setSuccessMessage(`Draft order set to "${method}"`);
        setTimeout(() => setSuccessMessage(''), 3000);
        setSelectedMethod(method);
        loadDraftState(false);
      } else {
        setError(response.message || 'Failed to set draft order');
      }
    } catch (err) {
      console.error('Error setting draft order:', err);
      setError(err.response?.data?.detail || 'Failed to set draft order');
    } finally {
      setIsSettingOrder(false);
    }
  };

  const handleSetClock = async (minutes) => {
    setIsSettingClock(true);
    setError('');

    try {
      const response = await leaguesAPI.setMinorLeagueDraftClock(leagueId, minutes);

      if (response.success) {
        setClockMinutes(minutes);
        setSuccessMessage(response.message);
        setTimeout(() => setSuccessMessage(''), 3000);
        loadDraftState(false);
      } else {
        setError(response.message || 'Failed to set clock');
      }
    } catch (err) {
      console.error('Error setting clock:', err);
      setError(err.response?.data?.detail || 'Failed to set clock');
    } finally {
      setIsSettingClock(false);
    }
  };

  const handleStartDraft = () => {
    showConfirm(
      'Start Draft',
      'Start the draft? Picks will begin immediately. Draft order cannot be changed after starting.',
      'Start Draft',
      async () => {
        closeConfirm();
        setIsStarting(true);
        setError('');

        try {
          const response = await leaguesAPI.startMinorLeagueDraft(leagueId);

          if (response.success) {
            setSuccessMessage('Draft started! First team is on the clock.');
            setTimeout(() => setSuccessMessage(''), 3000);
            loadDraftState();
          } else {
            setError(response.message || 'Failed to start draft');
          }
        } catch (err) {
          console.error('Error starting draft:', err);
          setError(err.response?.data?.detail || 'Failed to start draft');
        } finally {
          setIsStarting(false);
        }
      }
    );
  };

  const handlePlayerSelect = (player) => setSelectedPlayer(player);

  const handleMakePick = async (playerName, mlbTeam, position) => {
    if (!playerName || playerName.trim().length === 0) {
      setError('Player name is required');
      return;
    }

    setIsPickingPlayer(true);
    setError('');

    try {
      console.log('PICK PAYLOAD:', { playerName, mlbTeam, position });
      const response = await leaguesAPI.makeMinorLeagueDraftPick(
        leagueId, playerName.trim(), mlbTeam, position
      );

      if (response.success) {
        const pickInfo = response.pick ?? {};
        const nextPick = response.next_pick;
        const isPhantom = pickInfo.is_phantom || false;
        const teamName = pickInfo.team_name || '';
        let msg = `${playerName} drafted by ${teamName}`;
        if (isPhantom) msg += ' (Phantom — not yet in MLB database)';
        if (nextPick) {
          msg += ` — Next: ${nextPick.team_name} (Round ${nextPick.round_number}, Pick ${nextPick.pick_in_round})`;
        } else if (response.draft_status === 'completed') {
          msg += ' — Draft complete!';
        }
        setSuccessMessage(msg);
        setTimeout(() => setSuccessMessage(''), 15000);
        setSelectedPlayer(null);
        loadDraftState(false);
      } else {
        setError(response.message || 'Failed to make pick');
      }
    } catch (err) {
      console.error('Error making pick:', err);
      const errData = err.response?.data;
      const errMsg = errData?.error?.message || errData?.detail || err.message || 'Failed to make pick';
      setError(errMsg);
    } finally {
      setIsPickingPlayer(false);
    }
  };

  const handleMakePhantomPick = async (playerName, mlbTeam, position) => {
    if (!playerName || playerName.trim().length === 0) {
      setError('Player name is required');
      return;
    }

    setIsPickingPlayer(true);
    setError('');

    try {
      const response = await leaguesAPI.makePhantomDraftPick(
        leagueId, playerName.trim(), mlbTeam, position
      );

      if (response.success) {
        const pickInfo = response.pick ?? {};
        const nextPick = response.next_pick;
        let msg = `${playerName} (phantom) drafted by ${pickInfo.team_name || ''}`;
        if (nextPick) {
          msg += ` — Next: ${nextPick.team_name} (Round ${nextPick.round_number}, Pick ${nextPick.pick_in_round})`;
        } else if (response.draft_status === 'completed') {
          msg += ' — Draft complete!';
        }
        setSuccessMessage(msg);
        setTimeout(() => setSuccessMessage(''), 15000);
        setSelectedPlayer(null);
        loadDraftState(false);
      } else {
        setError(response.message || 'Failed to make phantom pick');
      }
    } catch (err) {
      console.error('Error making phantom pick:', err);
      const errData = err.response?.data;
      const errMsg = errData?.error?.message || errData?.detail || err.message || 'Failed to make phantom pick';
      setError(errMsg);
    } finally {
      setIsPickingPlayer(false);
    }
  };

  const handleCompleteDraft = () => {
    showConfirm(
      'Complete Draft',
      'Manually complete this draft? This will end the draft even if not all picks are made.',
      'Complete Draft',
      async () => {
        closeConfirm();
        setIsCompleting(true);
        setError('');

        try {
          const response = await leaguesAPI.completeMinorLeagueDraft(leagueId, selectedYear);

          if (response.success) {
            setSuccessMessage('Draft completed successfully!');
            setTimeout(() => setSuccessMessage(''), 3000);
            loadDraftState();
          } else {
            setError(response.message || 'Failed to complete draft');
          }
        } catch (err) {
          console.error('Error completing draft:', err);
          setError(err.response?.data?.detail || 'Failed to complete draft');
        } finally {
          setIsCompleting(false);
        }
      },
      'warning'
    );
  };

  // ========================================
  // COMMISSIONER OVERRIDE ACTIONS
  // ========================================
  const handleCommissionerPick = async (playerName, mlbTeam, position) => {
    if (!commissionerTeamId) {
      setError('Select a team to pick on behalf of');
      return;
    }
    if (!playerName || playerName.trim().length === 0) {
      setError('Player name is required');
      return;
    }

    setIsCommissionerPicking(true);
    setError('');

    try {
      const response = await leaguesAPI.commissionerMinorLeagueDraftPick(
        leagueId, playerName.trim(), commissionerTeamId, mlbTeam, position
      );

      if (response.success) {
        const pickInfo = response.pick ?? {};
        const isPhantom = pickInfo.is_phantom || false;
        setSuccessMessage(
          `Commissioner picked ${playerName} for ${pickInfo.team_name || 'team'}${isPhantom ? ' (Phantom)' : ''}`
        );
        setTimeout(() => setSuccessMessage(''), 5000);
        setCommissionerSelectedPlayer(null);
        setCommissionerTeamId('');
        loadDraftState(false);
      } else {
        setError(response.message || 'Failed to make pick');
      }
    } catch (err) {
      console.error('Error making commissioner pick:', err);
      setError(err.response?.data?.detail || 'Failed to make pick');
    } finally {
      setIsCommissionerPicking(false);
    }
  };

  const handleUndoPick = () => {
    const completedPicks = (draftState?.picks || []).filter(p => p.player_name);
    const lastPick = completedPicks.length > 0
      ? completedPicks[completedPicks.length - 1]
      : null;
    const pickDesc = lastPick
      ? `${lastPick.player_name} (pick #${lastPick.overall_pick})`
      : 'the last pick';

    showConfirm(
      'Undo Last Pick',
      `Undo ${pickDesc}? The player will be released and the pick pointer will roll back.`,
      'Undo Pick',
      async () => {
        closeConfirm();
        setIsUndoing(true);
        setError('');

        try {
          const response = await leaguesAPI.undoMinorLeagueDraftPick(leagueId, selectedYear);

          if (response.success) {
            setSuccessMessage(response.message || 'Pick undone successfully');
            setTimeout(() => setSuccessMessage(''), 4000);
            loadDraftState(false);
          } else {
            setError(response.message || 'Failed to undo pick');
          }
        } catch (err) {
          console.error('Error undoing pick:', err);
          setError(err.response?.data?.detail || 'Failed to undo pick');
        } finally {
          setIsUndoing(false);
        }
      },
      'warning'
    );
  };

  // ========================================
  // MANUAL ORDER HELPERS
  // ========================================
  const moveTeam = (index, direction) => {
    const order = manualOrder || draftState?.draft_order?.map(t => t.team_id) || [];
    const newOrder = [...order];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newOrder.length) return;
    [newOrder[index], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[index]];
    setManualOrder(newOrder);
  };

  const getOrderedTeams = () => {
    if (!draftState?.draft_order) return [];
    if (selectedMethod === 'manual' && manualOrder) {
      const lookup = {};
      draftState.draft_order.forEach(t => { lookup[t.team_id] = t; });
      return manualOrder.map((tid, i) => ({ ...lookup[tid], pick_order: i + 1 }));
    }
    return [...draftState.draft_order].sort((a, b) => a.pick_order - b.pick_order);
  };

  // ========================================
  // FORMAT DEADLINE (human-readable time remaining)
  // ========================================
  const formatDeadline = (date) => {
    if (!date) return null;
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const isPast = diff < 0;

    const options = { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    const formatted = date.toLocaleDateString('en-US', options);

    if (isPast) {
      return { text: `Overdue (was ${formatted})`, remaining: null, isPast: true };
    }

    // Human-readable time remaining
    const totalMinutes = Math.floor(diff / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    let remaining;
    if (days > 0) {
      remaining = `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''} remaining`;
    } else if (hours > 0) {
      remaining = `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''} remaining`;
    } else {
      remaining = `${minutes} minute${minutes !== 1 ? 's' : ''} remaining`;
    }

    return { text: formatted, remaining, isPast: false };
  };

  // ========================================
  // RENDER: STEPPER
  // ========================================
  const renderStepper = () => {
    const currentStep = getCurrentStep();

    return (
      <div className={`${dynastyTheme.components.card.base} p-4`}>
        <div className="flex items-center justify-between">
          {STEPS.map((step, idx) => (
            <React.Fragment key={step.key}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border-2 transition-all ${
                    idx < currentStep
                      ? 'bg-yellow-500 border-yellow-500 text-neutral-900'
                      : idx === currentStep
                      ? 'bg-yellow-500/20 border-yellow-400 text-yellow-400 animate-pulse'
                      : 'bg-neutral-800 border-neutral-600 text-neutral-500'
                  }`}
                >
                  {idx < currentStep ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    idx + 1
                  )}
                </div>
                <span
                  className={`text-xs mt-1.5 font-medium ${
                    idx <= currentStep
                      ? dynastyTheme.classes.text.white
                      : dynastyTheme.classes.text.neutralDark
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {idx < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 mt-[-20px] ${
                    idx < currentStep ? 'bg-yellow-500' : 'bg-neutral-700'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  // ========================================
  // RENDER: DRAFT ORDER CONFIG (Step 1)
  // ========================================
  const renderDraftOrderConfig = () => {
    if (!draftState || draftState.draft_status !== 'not_started') return null;

    const orderedTeams = getOrderedTeams();
    const currentMethod = draftState.draft_order_method || 'linear';

    return (
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white} mb-4 flex items-center gap-2`}>
          <Settings className={`w-6 h-6 ${dynastyTheme.classes.text.primary}`} />
          Draft Order Configuration
        </h3>

        {isCommissioner && (
          <div className="mb-6">
            <label className={`block text-sm font-medium ${dynastyTheme.classes.text.neutralLight} mb-2`}>
              Order Method
            </label>
            <div className="grid grid-cols-3 gap-2">
              {ORDER_METHODS.map(m => {
                const Icon = m.icon;
                const isActive = selectedMethod === m.value;
                return (
                  <button
                    key={m.value}
                    onClick={() => {
                      setSelectedMethod(m.value);
                      if (m.value === 'manual' && !manualOrder) {
                        setManualOrder(draftState.draft_order.map(t => t.team_id));
                      }
                    }}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      isActive
                        ? 'border-yellow-400 bg-yellow-500/10'
                        : 'border-neutral-700 bg-neutral-800 hover:border-neutral-500'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-4 h-4 ${isActive ? 'text-yellow-400' : 'text-neutral-400'}`} />
                      <span className={`text-sm font-medium ${isActive ? 'text-yellow-400' : dynastyTheme.classes.text.white}`}>
                        {m.label}
                      </span>
                    </div>
                    <span className={`text-xs ${dynastyTheme.classes.text.neutralDark}`}>
                      {m.description}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={() => handleSetDraftOrder(selectedMethod)}
                disabled={isSettingOrder}
                className={dynastyTheme.utils.getComponent('button', 'primary', 'sm')}
              >
                {isSettingOrder ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Shuffle className="w-4 h-4 mr-2" />
                    {selectedMethod === currentMethod ? 'Regenerate Order' : 'Apply Order'}
                  </>
                )}
              </button>
              <span className={`text-sm ${dynastyTheme.classes.text.neutralDark}`}>
                Current: <span className={dynastyTheme.classes.text.neutralLight}>{currentMethod}</span>
              </span>
            </div>

            {/* Pick Clock Setting */}
            <div className={`mt-6 pt-4 border-t ${dynastyTheme.classes.border.neutral}`}>
              <label className={`block text-sm font-medium ${dynastyTheme.classes.text.neutralLight} mb-2 flex items-center gap-2`}>
                <Clock className="w-4 h-4" />
                Pick Clock (optional)
              </label>
              <div className="flex items-center gap-3">
                <select
                  value={clockMinutes ?? ''}
                  onChange={(e) => {
                    const val = e.target.value === '' ? null : parseInt(e.target.value);
                    handleSetClock(val);
                  }}
                  disabled={isSettingClock}
                  className={`px-3 py-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} focus:outline-none`}
                >
                  {CLOCK_OPTIONS.map(opt => (
                    <option key={opt.label} value={opt.value ?? ''}>{opt.label}</option>
                  ))}
                </select>
                <span className={`text-xs ${dynastyTheme.classes.text.neutralDark}`}>
                  Time each team has to make their pick
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Email Notification Preferences (all owners) */}
        {notifPrefs && (
          <div className={`mt-6 pt-4 border-t ${dynastyTheme.classes.border.neutral}`}>
            <label className={`block text-sm font-medium ${dynastyTheme.classes.text.neutralLight} mb-3 flex items-center gap-2`}>
              <AlertTriangle className="w-4 h-4" />
              Email Notifications
            </label>
            <div className="space-y-2">
              {[
                { key: 'email_draft_your_turn', label: "It's my turn to pick" },
                { key: 'email_draft_started', label: 'Draft started' },
                { key: 'email_draft_completed', label: 'Draft completed' },
                { key: 'email_pick_skipped', label: 'My pick was skipped' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={notifPrefs[key] ?? true}
                    onChange={() => handleNotifPrefToggle(key)}
                    className="w-4 h-4 rounded border-neutral-600 text-yellow-500 focus:ring-yellow-500 bg-neutral-800"
                  />
                  <span className={`text-sm ${dynastyTheme.classes.text.neutralLight} group-hover:${dynastyTheme.classes.text.white}`}>
                    {label}
                  </span>
                </label>
              ))}
            </div>
            <p className={`text-xs ${dynastyTheme.classes.text.neutralDark} mt-2`}>
              Choose which draft events trigger an email to you.
            </p>
          </div>
        )}

        <div>
          <h4 className={`text-sm font-medium ${dynastyTheme.classes.text.neutralLight} mb-2`}>
            Pick Order
          </h4>
          <div className="space-y-1">
            {orderedTeams.map((team, idx) => (
              <div
                key={team.team_id}
                className={`flex items-center justify-between p-2 rounded ${
                  idx % 2 === 0 ? 'bg-neutral-800/50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-6 text-center text-sm font-mono ${dynastyTheme.classes.text.primary}`}>
                    {team.pick_order}
                  </span>
                  <span className={dynastyTheme.classes.text.white}>
                    {team.team_name}
                  </span>
                </div>
                {isCommissioner && selectedMethod === 'manual' && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => moveTeam(idx, -1)}
                      disabled={idx === 0}
                      className="p-1 rounded hover:bg-neutral-700 disabled:opacity-30"
                    >
                      <ChevronUp className="w-4 h-4 text-neutral-400" />
                    </button>
                    <button
                      onClick={() => moveTeam(idx, 1)}
                      disabled={idx === orderedTeams.length - 1}
                      className="p-1 rounded hover:bg-neutral-700 disabled:opacity-30"
                    >
                      <ChevronDown className="w-4 h-4 text-neutral-400" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {['snake', 'standings', 'lottery'].includes(currentMethod) && (
          <p className={`text-xs ${dynastyTheme.classes.text.neutralDark} mt-3`}>
            Snake reversal: even rounds will reverse this order.
          </p>
        )}
      </div>
    );
  };

  // ========================================
  // RENDER: START DRAFT BUTTON
  // ========================================
  const renderStartDraft = () => {
    if (!draftState || draftState.draft_status !== 'not_started' || !isCommissioner) return null;

    return (
      <div className={`${dynastyTheme.components.card.highlighted} p-6 border-2 border-yellow-500/30`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`text-lg font-bold ${dynastyTheme.classes.text.white} mb-1`}>
              Ready to Start?
            </h3>
            <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
              Once started, the draft order is locked and teams can begin making picks.
            </p>
          </div>
          <button
            onClick={handleStartDraft}
            disabled={isStarting}
            className={dynastyTheme.utils.getComponent('button', 'primary', 'lg')}
          >
            {isStarting ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Play className="w-5 h-5 mr-2" />
                Start Draft
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  // ========================================
  // RENDER: PROGRESS BAR
  // ========================================
  const renderDraftProgress = () => {
    if (!draftState || draftState.draft_status === 'not_started') return null;

    const totalPicks = (draftState.draft_order?.length || 0) * (draftState.total_rounds || 2);
    const picksMade = draftState.total_picks || 0;
    const progressPercent = totalPicks > 0 ? (picksMade / totalPicks) * 100 : 0;

    return (
      <div className={`${dynastyTheme.components.card.base} p-4`}>
        <div className="flex items-center justify-between mb-2">
          <span className={dynastyTheme.classes.text.neutralLight}>
            Draft Progress
          </span>
          <span className={dynastyTheme.classes.text.white}>
            {picksMade} / {totalPicks} picks
          </span>
        </div>
        <div className="w-full bg-neutral-700 rounded-full h-3 overflow-hidden">
          <div
            className="bg-gradient-to-r from-yellow-400 to-yellow-500 h-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>
    );
  };

  // ========================================
  // RENDER: CURRENT PICK + UPCOMING
  // ========================================
  const renderCurrentPick = () => {
    if (!draftState || draftState.draft_status !== 'in_progress') return null;

    const currentTurn = draftState.current_turn;
    if (!currentTurn) return null;

    const deadline = formatDeadline(pickDeadline);
    // Next picks (skip index 0 which is current)
    const nextPicks = upcomingPicks.slice(1, 6);

    return (
      <div className={`${dynastyTheme.components.card.highlighted} p-6 border-2 ${isMyTurn ? 'border-yellow-400' : 'border-neutral-700'}`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white} mb-2`}>
              {isMyTurn ? 'YOUR TURN!' : `Waiting for ${currentTurn.team_name}`}
            </h3>
            <div className="flex items-center gap-3">
              <Users className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
              <span className={`text-lg font-semibold ${dynastyTheme.classes.text.white}`}>
                {currentTurn.team_name || 'Unknown Team'}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.primary}`}>
                On the Clock
              </span>
            </div>
            <div className={`mt-2 ${dynastyTheme.classes.text.neutralLight}`}>
              Round {draftState.current_round} | Pick {draftState.current_pick_in_round} of {draftState.draft_order?.length}
              {draftState.draft_order_method && draftState.draft_order_method !== 'linear' && (
                <span className={`ml-2 ${dynastyTheme.classes.text.neutralDark}`}>
                  ({draftState.draft_order_method})
                </span>
              )}
            </div>

            {/* Async clock deadline */}
            {deadline && (
              <div className={`mt-2 ${deadline.isPast ? dynastyTheme.classes.text.error : dynastyTheme.classes.text.neutralLight}`}>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">
                    {deadline.isPast ? deadline.text : `Pick due by ${deadline.text}`}
                  </span>
                </div>
                {deadline.remaining && (
                  <div className="text-sm mt-1 ml-6 text-yellow-400 font-medium">
                    {deadline.remaining}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* "Picks away" badge or "Make Your Pick" */}
          <div className="text-right">
            {isMyTurn ? (
              <div className={`${dynastyTheme.classes.text.primary} font-bold text-lg`}>
                Make Your Pick!
              </div>
            ) : picksAwayFromMe !== null && picksAwayFromMe > 0 ? (
              <div className={`text-center px-4 py-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
                <div className={`text-2xl font-bold ${dynastyTheme.classes.text.primary}`}>
                  {picksAwayFromMe}
                </div>
                <div className={`text-xs ${dynastyTheme.classes.text.neutralDark}`}>
                  pick{picksAwayFromMe !== 1 ? 's' : ''} away
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Upcoming picks list */}
        {nextPicks.length > 0 && (
          <div className={`border-t ${dynastyTheme.classes.border.neutral} pt-3 mt-1`}>
            <h4 className={`text-xs font-medium ${dynastyTheme.classes.text.neutralDark} mb-2 uppercase tracking-wide`}>
              Upcoming Picks
            </h4>
            <div className="flex gap-2 flex-wrap">
              {nextPicks.map((pick, idx) => {
                const isMe = pick.teamId === myTeamId;
                return (
                  <div
                    key={`${pick.round}-${pick.pickInRound}`}
                    className={`px-3 py-1.5 rounded text-sm flex items-center gap-2 ${
                      isMe
                        ? 'bg-yellow-500/15 border border-yellow-500/30'
                        : 'bg-neutral-800/60'
                    }`}
                  >
                    <span className={`text-xs font-mono ${dynastyTheme.classes.text.neutralDark}`}>
                      {pick.round}.{pick.pickInRound}
                    </span>
                    <span className={isMe ? dynastyTheme.classes.text.primary : dynastyTheme.classes.text.neutralLight}>
                      {pick.teamName}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ========================================
  // RENDER: COMMISSIONER CONTROLS
  // ========================================
  const renderCommissionerControls = () => {
    if (!isCommissioner || !draftState || draftState.draft_status !== 'in_progress') return null;

    const hasPicks = draftState.picks && draftState.picks.length > 0;

    return (
      <div className={`${dynastyTheme.components.card.base} p-6`}>
        <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white} mb-4 flex items-center gap-2`}>
          <Shield className={`w-6 h-6 ${dynastyTheme.classes.text.warning}`} />
          Commissioner Controls
        </h3>

        {/* Pick on behalf of team */}
        <div className="mb-6">
          <h4 className={`text-sm font-medium ${dynastyTheme.classes.text.neutralLight} mb-3`}>
            Pick on Behalf of Team
          </h4>

          <div className="mb-3">
            <select
              value={commissionerTeamId}
              onChange={(e) => setCommissionerTeamId(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral} focus:outline-none`}
            >
              <option value="">Select a team...</option>
              {(draftState.draft_order || [])
                .sort((a, b) => a.pick_order - b.pick_order)
                .map(team => (
                  <option key={team.team_id} value={team.team_id}>
                    {team.team_name}
                  </option>
                ))
              }
            </select>
          </div>

          {commissionerTeamId && (
            <PlayerSearchWithManual
              leagueId={leagueId}
              onPlayerSelect={(player) => setCommissionerSelectedPlayer(player)}
              onMakePick={handleCommissionerPick}
              onMakePhantomPick={handleMakePhantomPick}
              selectedPlayer={commissionerSelectedPlayer}
              isPickingPlayer={isCommissionerPicking}
              draftedPlayerIds={draftedPlayerIds}
            />
          )}
        </div>

        {/* Undo + Complete buttons */}
        <div className={`flex items-center gap-4 pt-4 border-t ${dynastyTheme.classes.border.neutral}`}>
          <button
            onClick={handleUndoPick}
            disabled={isUndoing || !hasPicks}
            className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
          >
            {isUndoing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Undoing...
              </>
            ) : (
              <>
                <Undo2 className="w-4 h-4 mr-2" />
                Undo Last Pick
              </>
            )}
          </button>

          <button
            onClick={handleCompleteDraft}
            disabled={isCompleting}
            className={dynastyTheme.utils.getComponent('button', 'warning', 'md')}
          >
            {isCompleting ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Completing...
              </>
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" />
                Complete Draft Now
              </>
            )}
          </button>

          <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>
            Manually end the draft (even if not all picks made)
          </span>
        </div>
      </div>
    );
  };

  // ========================================
  // MAIN RENDER
  // ========================================
  return (
    <div className="space-y-6">
      {/* CONFIRM MODAL */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        confirmVariant={confirmModal.confirmVariant}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />

      <TeamPageHeader
        title="Minor League Draft"
        logoUrl={userTeam?.team_logo_url}
        teamName={userTeam?.team_name}
        teamColors={userTeam?.team_colors}
        teamMotto={userTeam?.team_motto}
        badges={false}
      />

      {/* Draft Controls */}
      <div className="flex items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          <span className={`text-xs ${dynastyTheme.classes.text.neutralDark}`}>Auto</span>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`relative w-10 h-5 rounded-full transition-colors ${
              autoRefresh ? 'bg-yellow-500' : 'bg-neutral-600'
            }`}
            title={autoRefresh ? 'Auto-refresh on (5s)' : 'Auto-refresh off'}
          >
            <div
              className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                autoRefresh ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
        <button
          onClick={() => loadDraftState(true)}
          disabled={loading}
          className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* STEPPER */}
      {!loading && renderStepper()}

      {/* SUCCESS/ERROR MESSAGES */}
      {successMessage && (
        <div className="p-5 rounded-lg border-2 border-green-500 bg-green-500/10 flex items-center gap-3">
          <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
          <p className="text-green-300 font-semibold text-base">{successMessage}</p>
        </div>
      )}
      {error && (
        <div className="p-5 rounded-lg border-2 border-red-500 bg-red-500/10 flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0" />
          <p className="text-red-300 font-semibold text-base">{error}</p>
        </div>
      )}

      {/* LOADING STATE */}
      {loading && (
        <div className={`flex items-center justify-center py-12 ${dynastyTheme.components.card.base}`}>
          <RefreshCw className={`w-6 h-6 animate-spin ${dynastyTheme.classes.text.primary} mr-2`} />
          <span className={dynastyTheme.classes.text.white}>Loading draft...</span>
        </div>
      )}

      {/* STEP 0: NO DRAFT — INITIALIZE */}
      {!loading && !draftState && (
        <div className={`${dynastyTheme.components.card.base} p-12 text-center`}>
          <Gavel className={`w-16 h-16 mx-auto mb-4 ${dynastyTheme.classes.text.neutralDark}`} />
          <h2 className={`text-2xl font-bold ${dynastyTheme.classes.text.white} mb-4`}>
            No Draft Created Yet
          </h2>
          <p className={`${dynastyTheme.classes.text.neutralLight} mb-6 max-w-2xl mx-auto`}>
            The commissioner can initialize the {selectedYear} Minor League Draft.
            This will be a 2-round draft where teams can add prospects to their Minor League rosters.
          </p>

          {isCommissioner ? (
            <button
              onClick={handleInitializeDraft}
              disabled={isInitializing}
              className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
            >
              {isInitializing ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Initializing...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5 mr-2" />
                  Initialize Draft
                </>
              )}
            </button>
          ) : (
            <p className={`${dynastyTheme.classes.text.neutralDark} text-sm`}>
              Only the commissioner can initialize the draft.
            </p>
          )}
        </div>
      )}

      {/* STEP 1: NOT STARTED — ORDER CONFIG + START */}
      {!loading && draftState && draftState.draft_status === 'not_started' && (
        <>
          {renderDraftOrderConfig()}
          {renderStartDraft()}
        </>
      )}

      {/* STEPS 2-3: IN PROGRESS OR COMPLETED */}
      {!loading && draftState && draftState.draft_status !== 'not_started' && (
        <>
          {/* PROGRESS BAR */}
          {renderDraftProgress()}

          {/* CURRENT PICK + UPCOMING */}
          {renderCurrentPick()}

          {/* PLAYER SEARCH (only show if it's my turn) */}
          {isMyTurn && draftState.draft_status === 'in_progress' && (
            <div className={`${dynastyTheme.components.card.base} p-6`}>
              <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white} mb-4 flex items-center gap-2`}>
                <Search className={`w-6 h-6 ${dynastyTheme.classes.text.primary}`} />
                Make Your Pick
              </h3>

              <PlayerSearchWithManual
                leagueId={leagueId}
                onPlayerSelect={handlePlayerSelect}
                onMakePick={handleMakePick}
                onMakePhantomPick={handleMakePhantomPick}
                selectedPlayer={selectedPlayer}
                isPickingPlayer={isPickingPlayer}
                draftedPlayerIds={draftedPlayerIds}
              />
            </div>
          )}

          {/* DRAFT BOARD */}
          <div className={`${dynastyTheme.components.card.base} p-6`}>
            <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white} mb-4 flex items-center gap-2`}>
              <Trophy className={`w-6 h-6 ${dynastyTheme.classes.text.primary}`} />
              Draft Board
            </h3>

            <DraftBoard
              draftState={draftState}
              currentTurnInfo={currentTurnInfo}
            />
          </div>

          {/* COMMISSIONER CONTROLS */}
          {renderCommissionerControls()}

          {/* DRAFT COMPLETED MESSAGE */}
          {draftState.draft_status === 'completed' && (
            <div className={`${dynastyTheme.components.card.base} p-12 text-center`}>
              <CheckCircle className={`w-16 h-16 mx-auto mb-4 ${dynastyTheme.classes.text.success}`} />
              <h2 className={`text-2xl font-bold ${dynastyTheme.classes.text.white} mb-2`}>
                Draft Completed!
              </h2>
              <p className={`${dynastyTheme.classes.text.neutralLight}`}>
                The {selectedYear} Minor League Draft has been completed.
                All drafted players have been added to team rosters.
              </p>
              {draftState.phantom_picks > 0 && (
                <p className={`${dynastyTheme.classes.text.warning} mt-4`}>
                  {draftState.phantom_picks} phantom player{draftState.phantom_picks !== 1 ? 's' : ''} drafted
                  (not yet in MLB database)
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MinorLeagueDraft;
